import { useState, useEffect, useCallback } from "react";
import { Users, UserPlus, Search, RefreshCw, Link as LinkIcon, Edit, Loader2, X, IdCard, Ban, PlayCircle, Archive, Trash2, MoreVertical, Undo2, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import MembershipCardDialog from "../components/MembershipCardDialog";
import {
  getMemberships,
  createMembership,
  updateMembership,
  renewMembership,
  linkMembershipUser,
  getAdminUsers,
  getPartners,
  suspendMembership,
  reactivateMembership,
  archiveMembership,
  hardDeleteMembership,
  refundMembershipPayment,
  requestMembershipPayment,
} from "../lib/api";

// Helpers date : ajoute 1 an - 1 jour a une date ISO (YYYY-MM-DD)
function addYearMinusDay(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  // +1 an, -1 jour (avec gestion 29/02)
  let ny = y + 1, nm = m, nd = d;
  const daysInMonth = new Date(ny, nm, 0).getDate();
  if (nd > daysInMonth) nd = daysInMonth;
  const end = new Date(Date.UTC(ny, nm - 1, nd));
  end.setUTCDate(end.getUTCDate() - 1);
  const yy = end.getUTCFullYear();
  const mm = String(end.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(end.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

const STATUS_LABELS = {
  active: "Actif",
  pending: "En attente",
  expired: "Expiré",
  suspended: "Suspendu",
  archived: "Archivé",
};

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  expired: "bg-gray-100 text-gray-700",
  suspended: "bg-red-100 text-red-700",
  archived: "bg-gray-200 text-gray-600 line-through",
};

const TYPE_LABELS = {
  standard: "Adherent",
  founder: "Membre fondateur",
  partner: "Partenaire",
  donor_partner: "Partenaire donateur",
};

const TYPE_COLORS = {
  standard: "bg-gray-100 text-gray-800",
  founder: "bg-amber-100 text-amber-900",
  partner: "bg-blue-100 text-blue-800",
  donor_partner: "bg-violet-100 text-violet-800",
};

const PRICING_LABELS = {
  standard: "Normal",
  discounted: "Reduit",
  complimentary: "Offerte",
};

const PAYMENT_STATUS_META = {
  paid:     { label: "Payé",       color: "bg-emerald-100 text-emerald-800" },
  unpaid:   { label: "À payer",    color: "bg-orange-100 text-orange-800" },
  refunded: { label: "Remboursé",  color: "bg-blue-100 text-blue-800" },
  lifetime: { label: "À vie",      color: "bg-amber-100 text-amber-800" },
  failed:   { label: "Échec",      color: "bg-red-100 text-red-800" },
  legacy:   { label: "Manuel",     color: "bg-gray-100 text-gray-700" },
};

const emptyForm = {
  civility: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address_line1: "",
  postal_code: "",
  city: "",
  country: "France",
  subscription_date: "",
  valid_from: "",
  valid_until: "",
  status: "active",
  notes: "",
  membership_type: "standard",
  validity_type: "annual",
  pricing_type: "standard",
  discount_percent: "",
  fee_override: "",
  partner_id: "",
};

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return format(new Date(iso), "dd MMM yyyy", { locale: fr });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function MembershipsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [partners, setPartners] = useState([]);
  const [modal, setModal] = useState(null); // null | "create" | { mode: "edit"|"renew", membership }
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [cardOpenId, setCardOpenId] = useState(null);

  useEffect(() => {
    getPartners().then((r) => setPartners(r.data || [])).catch(() => setPartners([]));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.membership_type = typeFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await getMemberships(params);
      setList(data || []);
    } catch (e) {
      toast.error("Erreur de chargement des adherents");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setModal("create");
  };

  const openEdit = (m) => {
    setForm({ ...emptyForm, ...m });
    setModal({ mode: "edit", membership: m });
  };

  const openRenew = (m) => {
    // Preremplir avec +1 an depuis maintenant
    const now = new Date();
    const next = new Date();
    next.setFullYear(next.getFullYear() + 1);
    setForm({
      ...emptyForm,
      ...m,
      valid_from: now.toISOString().slice(0, 10),
      valid_until: next.toISOString().slice(0, 10),
    });
    setModal({ mode: "renew", membership: m });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === "create") {
        const payload = {
          civility: form.civility || null,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email.toLowerCase(),
          phone: form.phone || null,
          address_line1: form.address_line1 || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
          country: form.country || "France",
          subscription_date: form.subscription_date || null,
          valid_from: form.valid_from || null,
          valid_until: form.validity_type === "lifetime" ? null : (form.valid_until || null),
          status: form.status || "active",
          notes: form.notes || null,
          membership_type: form.membership_type || "standard",
          validity_type: form.validity_type || "annual",
          pricing_type: form.pricing_type || "standard",
          discount_percent: form.discount_percent === "" ? null : parseFloat(form.discount_percent),
          fee_override: form.fee_override === "" ? null : parseFloat(form.fee_override),
          partner_id: form.partner_id || null,
        };
        const { data } = await createMembership(payload);
        const linked = data.user_id ? " (rattache a un utilisateur existant)" : "";
        toast.success(`Adherent cree : ${data.member_number}${linked}`);
      } else if (modal.mode === "edit") {
        const payload = {
          civility: form.civility || null,
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email ? form.email.toLowerCase() : null,
          phone: form.phone || null,
          address_line1: form.address_line1 || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
          country: form.country || null,
          status: form.status || null,
          notes: form.notes || null,
          membership_type: form.membership_type || null,
          validity_type: form.validity_type || null,
          pricing_type: form.pricing_type || null,
          discount_percent: form.discount_percent === "" ? null : parseFloat(form.discount_percent),
          fee_override: form.fee_override === "" ? null : parseFloat(form.fee_override),
          partner_id: form.partner_id || null,
          valid_until: form.validity_type === "lifetime" ? null : (form.valid_until || null),
        };
        await updateMembership(modal.membership.id, payload);
        toast.success("Adherent mis a jour");
      } else if (modal.mode === "renew") {
        await renewMembership(modal.membership.id, {
          valid_from: form.valid_from,
          valid_until: form.valid_until,
        });
        toast.success(`Adhesion renouvelee : ${modal.membership.member_number}`);
      }
      setModal(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkUser = async (m) => {
    // Cherche par email
    if (!m.email) {
      toast.error("Aucun email pour rechercher un utilisateur");
      return;
    }
    try {
      const { data } = await getAdminUsers({ search: m.email });
      const match = (data || []).find((u) => u.email?.toLowerCase() === m.email.toLowerCase());
      if (!match) {
        toast.info(`Aucun utilisateur avec ${m.email}`);
        return;
      }
      if (!window.confirm(`Rattacher a ${match.name} (${match.email}) ?`)) return;
      await linkMembershipUser(m.id, match.id);
      toast.success("Utilisateur rattache");
      load();
    } catch {
      toast.error("Impossible de rattacher l'utilisateur");
    }
  };

  const handleSuspend = async (m) => {
    if (!window.confirm(`Suspendre la carte ${m.member_number} ? Le QR deviendra immédiatement non valide.`)) return;
    try {
      await suspendMembership(m.id);
      toast.success("Adhésion suspendue");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleReactivate = async (m) => {
    if (!window.confirm(`Réactiver la carte ${m.member_number} ?`)) return;
    try {
      await reactivateMembership(m.id);
      toast.success("Adhésion réactivée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleArchive = async (m) => {
    if (!window.confirm(
      `Archiver l'adhésion ${m.member_number} ?\n\n` +
      `Le numéro d'adhérent est conservé (jamais recyclé), l'historique et le QR sont préservés.\n` +
      `La carte devient non valide. Action réversible via « Réactiver ».`
    )) return;
    try {
      await archiveMembership(m.id);
      toast.success("Adhésion archivée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleRefundPayment = async (m) => {
    const p = m.payment || {};
    if (!p.external_transaction_id) {
      toast.error("Aucune transaction SumUp associée");
      return;
    }
    const msg = [
      `Rembourser le paiement SumUp Sandbox pour ${m.member_number} ?`,
      `Montant : ${p.payment_amount?.toFixed(2)} ${p.payment_currency}`,
      `Transaction : ${p.external_transaction_id}`,
      "",
      "⚠️ Ce remboursement ne suspend PAS automatiquement l'adhésion.",
      "Vous pourrez suspendre / archiver séparément ensuite si nécessaire."
    ].join("\n");
    if (!window.confirm(msg)) return;
    try {
      const { data } = await refundMembershipPayment(m.id, "admin_action");
      if (data.already_refunded) toast.info("Ce paiement était déjà remboursé");
      else toast.success(`Remboursement demandé (${data.refund_status || "requested"})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur remboursement");
    }
  };

  const handleRequestPayment = async (m) => {
    const label = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.member_number;
    const msg = [
      `Créer un lien de paiement SumUp Sandbox pour :`,
      `  ${label}`,
      `  ${m.public_member_id || m.member_number}`,
      "",
      "Motif : Cotisation annuelle",
      "Montant : 39,00 EUR (modifiable dans la modale)",
    ].join("\n");
    if (!window.confirm(msg)) return;
    try {
      const { data } = await requestMembershipPayment(m.id, {});
      if (data.checkout_url) {
        toast.success(`Lien de paiement créé (${data.amount} ${data.currency})`);
        window.open(data.checkout_url, "_blank");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création paiement");
    }
  };

  const handleHardDelete = async (m) => {    const confirm = window.prompt(
      `⚠️ SUPPRESSION DÉFINITIVE de l'adhésion ${m.member_number}.\n\n` +
      `Cette action est irréversible et ne doit être utilisée QUE pour les fiches créées par erreur.\n` +
      `L'archivage est l'action standard.\n\n` +
      `Pour confirmer, saisissez exactement le numéro d'adhérent : ${m.member_number}`
    );
    if (!confirm) return;
    if (confirm.trim() !== m.member_number) {
      toast.error("Le numéro saisi ne correspond pas — suppression annulée");
      return;
    }
    try {
      await hardDeleteMembership(m.id, confirm.trim());
      toast.success("Adhésion supprimée définitivement");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const modalTitle =
    modal === "create"
      ? "Nouvel adherent"
      : modal?.mode === "edit"
      ? `Modifier ${modal.membership.member_number}`
      : modal?.mode === "renew"
      ? `Renouveler ${modal.membership.member_number}`
      : "";

  const isRenewMode = modal?.mode === "renew";

  return (
    <div data-testid="memberships-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">Adherents</h1>
          <p className="text-muted-foreground">
            {list.length} adherent(s){statusFilter !== "all" ? ` - filtre ${STATUS_LABELS[statusFilter]}` : ""}
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-membership-btn">
          <UserPlus className="h-4 w-4" /> Nouvel adherent
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, prenom, email, telephone ou numero d'adherent..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-memberships-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter-memberships">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-[200px]" data-testid="type-filter-memberships">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">Aucun adherent</h3>
            <p className="text-muted-foreground">Cliquez sur "Nouvel adherent" pour en creer un</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto" data-testid="memberships-table">
              <thead className="bg-muted/50 border-b border-border text-xs uppercase tracking-wide font-medium text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">N°</th>
                  <th className="px-3 py-2 text-left">Adhérent</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Type</th>
                  <th className="px-3 py-2 text-left hidden md:table-cell">Email / Tel</th>
                  <th className="px-3 py-2 text-left hidden lg:table-cell whitespace-nowrap">Adhésion</th>
                  <th className="px-3 py-2 text-left hidden xl:table-cell whitespace-nowrap">Validité</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Statut</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Paiement</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap hidden sm:table-cell">Compte</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {list.map((m) => (
                  <tr key={m.id} className="hover:bg-muted/20" data-testid={`membership-row-${m.id}`}>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{m.public_member_id || m.member_number || "-"}</td>
                    <td className="px-3 py-2 min-w-0">
                      {(m.first_name || m.last_name) ? (
                        <div className="font-medium text-primary truncate max-w-[220px]" data-testid={`membership-identity-${m.id}`}>
                          {[m.civility, m.first_name, m.last_name].filter(Boolean).join(" ")}
                        </div>
                      ) : (
                        <div className="text-xs italic text-muted-foreground">Identité à renseigner</div>
                      )}
                      {m.city ? <div className="text-xs text-muted-foreground truncate max-w-[220px]">{m.city}</div> : null}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          TYPE_COLORS[m.membership_type || "standard"] || "bg-gray-100 text-gray-700"
                        }`}
                        data-testid={`membership-type-${m.id}`}
                      >
                        {TYPE_LABELS[m.membership_type || "standard"]}
                      </span>
                      {m.pricing_type && m.pricing_type !== "standard" ? (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {PRICING_LABELS[m.pricing_type]}
                          {m.pricing_type === "discounted" && m.discount_percent != null ? ` -${m.discount_percent}%` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell min-w-0">
                      <div className="truncate max-w-[220px]" title={m.email}>{m.email}</div>
                      {m.phone ? <div className="text-xs text-muted-foreground truncate max-w-[220px]">{m.phone}</div> : null}
                    </td>
                    <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                      {fmtDate(m.subscription_date)}
                    </td>
                    <td className="px-3 py-2 hidden xl:table-cell text-xs text-muted-foreground whitespace-nowrap">
                      {m.validity_type === "lifetime"
                        ? <span className="text-amber-800 font-medium">Permanente</span>
                        : m.validity_type === "partnership"
                        ? <span className="text-blue-800">Tant que partenariat</span>
                        : fmtDate(m.valid_until)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          STATUS_COLORS[m.status] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {STATUS_LABELS[m.status] || m.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`payment-cell-${m.id}`}>
                      {(() => {
                        const ps = m.payment?.payment_status || "legacy";
                        const meta = PAYMENT_STATUS_META[ps] || PAYMENT_STATUS_META.legacy;
                        return (
                          <div>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>
                              {meta.label}
                            </span>
                            {m.payment?.payment_amount != null ? (
                              <div className="text-[10px] text-muted-foreground mt-1">
                                {m.payment.payment_amount.toFixed(2)} {m.payment.payment_currency}
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap hidden sm:table-cell">
                      {m.user_id ? (
                        <span className="text-green-700">Rattaché</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setCardOpenId(m.id)}
                          data-testid={`card-membership-${m.id}`}
                          title="Voir la carte"
                          className="h-8 w-8"
                        >
                          <IdCard className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEdit(m)}
                          data-testid={`edit-membership-${m.id}`}
                          title="Modifier"
                          className="h-8 w-8"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" title="Autres actions" data-testid={`actions-menu-${m.id}`} className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {(m.validity_type || "annual") === "annual" ? (
                              <DropdownMenuItem
                                onClick={() => openRenew(m)}
                                data-testid={`renew-membership-${m.id}`}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" /> Renouveler l'année
                              </DropdownMenuItem>
                            ) : null}
                            {m.status === "active" ? (
                              <DropdownMenuItem
                                onClick={() => handleSuspend(m)}
                                data-testid={`suspend-membership-${m.id}`}
                              >
                                <Ban className="h-4 w-4 mr-2" /> Suspendre
                              </DropdownMenuItem>
                            ) : null}
                            {(m.status === "suspended" || m.status === "archived") ? (
                              <DropdownMenuItem
                                onClick={() => handleReactivate(m)}
                                data-testid={`reactivate-membership-${m.id}`}
                              >
                                <PlayCircle className="h-4 w-4 mr-2" /> Réactiver
                              </DropdownMenuItem>
                            ) : null}
                            {!m.user_id ? (
                              <DropdownMenuItem
                                onClick={() => handleLinkUser(m)}
                                data-testid={`link-user-${m.id}`}
                              >
                                <LinkIcon className="h-4 w-4 mr-2" /> Rattacher un utilisateur
                              </DropdownMenuItem>
                            ) : null}
                            {(m.payment?.external_transaction_id && !m.payment?.refund_id) ? (
                              <DropdownMenuItem
                                onClick={() => handleRefundPayment(m)}
                                data-testid={`refund-membership-${m.id}`}
                              >
                                <Undo2 className="h-4 w-4 mr-2" /> Rembourser le paiement
                              </DropdownMenuItem>
                            ) : null}
                            {(["legacy","unpaid","failed"].includes(m.payment?.payment_status) && m.validity_type === "annual" && !["founder","partner"].includes(m.membership_type) && (m.pricing_type !== "complimentary")) ? (
                              <DropdownMenuItem
                                onClick={() => handleRequestPayment(m)}
                                data-testid={`request-payment-${m.id}`}
                              >
                                <CreditCard className="h-4 w-4 mr-2" /> Demander un paiement
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator />
                            {m.status !== "archived" ? (
                              <DropdownMenuItem
                                onClick={() => handleArchive(m)}
                                className="text-amber-700"
                                data-testid={`archive-membership-${m.id}`}
                              >
                                <Archive className="h-4 w-4 mr-2" /> Archiver
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleHardDelete(m)}
                              className="text-red-700"
                              data-testid={`hard-delete-membership-${m.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Suppression définitive…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="membership-modal">
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-heading text-xl font-semibold">{modalTitle}</h2>
              <Button variant="ghost" size="icon" onClick={() => setModal(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {isRenewMode ? (
                <>
                  <div className="text-sm text-muted-foreground">
                    Numero conserve : <span className="font-mono font-semibold">{modal.membership.member_number}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Valide a partir de *</label>
                      <Input
                        type="date"
                        required
                        value={(form.valid_from || "").slice(0, 10)}
                        onChange={(e) => {
                          const vf = e.target.value;
                          const next = { ...form, valid_from: vf };
                          if (vf && (!form.valid_until_touched)) {
                            next.valid_until = addYearMinusDay(vf);
                          }
                          setForm(next);
                        }}
                        data-testid="renew-valid-from"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Valide jusqu'a *</label>
                      <Input
                        type="date"
                        required
                        value={(form.valid_until || "").slice(0, 10)}
                        onChange={(e) => setForm({ ...form, valid_until: e.target.value, valid_until_touched: true })}
                        data-testid="renew-valid-until"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Civilite</label>
                      <Select value={form.civility || ""} onValueChange={(v) => setForm({ ...form, civility: v })}>
                        <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M.">M.</SelectItem>
                          <SelectItem value="Mme">Mme</SelectItem>
                          <SelectItem value="Autre">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Prenom *</label>
                      <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="mship-first-name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Nom *</label>
                      <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="mship-last-name" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email *</label>
                      <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="mship-email" />
                      {modal === "create" ? (
                        <p className="text-xs text-muted-foreground mt-1">Un rattachement automatique sera tente si un utilisateur existe avec cet email.</p>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Telephone</label>
                      <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Adresse</label>
                    <Input value={form.address_line1 || ""} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} placeholder="Rue et numero" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Code postal</label>
                      <Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Ville</label>
                      <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Pays</label>
                      <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Date d'adhesion</label>
                      <Input
                        type="date"
                        value={(form.subscription_date || "").slice(0, 10)}
                        onChange={(e) => setForm({ ...form, subscription_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Valide a partir de {form.validity_type === "annual" ? "*" : ""}</label>
                      <Input
                        type="date"
                        required={form.validity_type === "annual"}
                        value={(form.valid_from || "").slice(0, 10)}
                        onChange={(e) => {
                          const vf = e.target.value;
                          const next = { ...form, valid_from: vf };
                          if (form.validity_type === "annual" && vf && !form.valid_until_touched) {
                            next.valid_until = addYearMinusDay(vf);
                          }
                          setForm(next);
                        }}
                        data-testid="mship-valid-from"
                      />
                      {form.validity_type === "annual" && !form.valid_from ? (
                        <p className="text-xs text-amber-700 mt-1">Date de validité à renseigner</p>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Valide jusqu'a</label>
                      <Input
                        type="date"
                        value={form.validity_type === "lifetime" ? "" : (form.valid_until || "").slice(0, 10)}
                        disabled={form.validity_type === "lifetime"}
                        placeholder={form.validity_type === "lifetime" ? "Permanente" : "Auto (+1 an - 1 jour)"}
                        onChange={(e) => setForm({ ...form, valid_until: e.target.value, valid_until_touched: true })}
                        data-testid="mship-valid-until"
                      />
                      {form.validity_type === "annual" && form.valid_from && !form.valid_until_touched ? (
                        <p className="text-xs text-muted-foreground mt-1">Calculé automatiquement (modifiable).</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 border-t border-border pt-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Type de carte</label>
                      <Select
                        value={form.membership_type || "standard"}
                        onValueChange={(v) => {
                          const next = { ...form, membership_type: v };
                          if (v === "founder") next.validity_type = "lifetime";
                          else if (v === "partner" || v === "donor_partner") next.validity_type = "partnership";
                          else if (form.membership_type === "founder" || form.membership_type === "partner" || form.membership_type === "donor_partner") next.validity_type = "annual";
                          setForm(next);
                        }}
                      >
                        <SelectTrigger data-testid="mship-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(TYPE_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.membership_type === "donor_partner" ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          La gratuite peut etre proposee par defaut mais reste modifiable — decision a l'appreciation de l'admin.
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Validite</label>
                      <Select value={form.validity_type || "annual"} onValueChange={(v) => setForm({ ...form, validity_type: v })}>
                        <SelectTrigger data-testid="mship-validity"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="annual">Annuelle</SelectItem>
                          <SelectItem value="lifetime">Permanente</SelectItem>
                          <SelectItem value="partnership">Tant que partenariat</SelectItem>
                        </SelectContent>
                      </Select>
                      {form.validity_type === "lifetime" ? (
                        <p className="text-xs text-muted-foreground mt-1">Date de fin desactivee — carte permanente.</p>
                      ) : null}
                    </div>
                  </div>
                  {(form.membership_type === "partner" || form.membership_type === "donor_partner") ? (
                    <div>
                      <label className="text-sm font-medium mb-1 block">Partenaire associe (facultatif)</label>
                      <Select value={form.partner_id || "__none__"} onValueChange={(v) => setForm({ ...form, partner_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="mship-partner-id"><SelectValue placeholder="Aucun" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Aucun —</SelectItem>
                          {partners.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                  <div className="border-t border-border pt-4">
                    <label className="text-sm font-medium mb-2 block">Conditions d'adhesion</label>
                    <div className="flex gap-4 mb-3">
                      {Object.entries(PRICING_LABELS).map(([k, v]) => (
                        <label key={k} className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="pricing_type"
                            value={k}
                            checked={(form.pricing_type || "standard") === k}
                            onChange={() => setForm({ ...form, pricing_type: k, ...(k !== "discounted" ? { discount_percent: "", fee_override: "" } : {}) })}
                            data-testid={`mship-pricing-${k}`}
                          />
                          <span className="text-sm">{v}</span>
                        </label>
                      ))}
                    </div>
                    {form.pricing_type === "discounted" ? (
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Pourcentage de reduction (%)</label>
                          <Input
                            type="number" step="1" min="0" max="100"
                            value={form.discount_percent}
                            onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                            data-testid="mship-discount-percent"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Ou montant personnalise (EUR)</label>
                          <Input
                            type="number" step="0.01" min="0"
                            value={form.fee_override}
                            onChange={(e) => setForm({ ...form, fee_override: e.target.value })}
                            data-testid="mship-fee-override"
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Statut</label>
                    <Select value={form.status || "active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Notes internes</label>
                    <textarea
                      className="w-full min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                      value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button type="button" variant="ghost" onClick={() => setModal(null)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="save-membership-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {cardOpenId && (
        <MembershipCardDialog membershipId={cardOpenId} onClose={() => setCardOpenId(null)} />
      )}
    </div>
  );
}
